import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  inject,
} from '@angular/core';
import {
  AbstractControl,
  NonNullableFormBuilder,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';

import { SEX_OPTIONS } from '../../../shared/components/ui/pretty-select/common-options';
import { PrettySelectComponent } from '../../../shared/components/ui/pretty-select/pretty-select.component';
import { FormMode } from '../../../shared/types/forms.types';
import {
  CreateUserRequest,
  PatchUserRequest,
  SexEnum,
  User,
} from '../../../shared/types/user.types';

/**
 * Output emitted on submit. Caller casts to `CreateUserRequest` (create mode)
 * or `PatchUserRequest` (edit mode) — the fields are aligned with both.
 */
export type UserFormPayload = CreateUserRequest | PatchUserRequest;

@Component({
  selector: 'app-user-form',
  imports: [CommonModule, ReactiveFormsModule, PrettySelectComponent],
  templateUrl: './user-form.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserFormComponent implements OnChanges {
  private readonly fb = inject(NonNullableFormBuilder);

  readonly sexOptions = SEX_OPTIONS;

  /** Pre-fill (edit mode). Leave undefined for create. */
  @Input() user: User | null = null;
  @Input() mode: FormMode = 'create';
  @Input() submitting = false;
  @Input() serverError: string | null = null;

  @Output() readonly save = new EventEmitter<UserFormPayload>();
  @Output() readonly cancel = new EventEmitter<void>();

  showPassword = false;

  readonly form = this.fb.group({
    firstName: ['', [Validators.required, Validators.maxLength(80)]],
    lastName: ['', [Validators.required, Validators.maxLength(80)]],
    sex: this.fb.control<SexEnum | ''>(''),
    username: [
      '',
      [
        Validators.required,
        Validators.minLength(3),
        Validators.maxLength(40),
        Validators.pattern(/^[a-zA-Z0-9._-]+$/),
      ],
    ],
    email: ['', [Validators.required, Validators.email, Validators.maxLength(120)]],
    phone: ['', [Validators.required, Validators.maxLength(20)]],
    address: ['', [Validators.maxLength(255)]],
    plainPassword: [''], // validators set dynamically in ngOnChanges
    active: [true],
  });

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['mode']) {
      // Password is required only when creating.
      const pwdCtrl = this.form.controls.plainPassword;
      if (this.mode === 'create') {
        pwdCtrl.setValidators([Validators.required, Validators.minLength(6)]);
      } else {
        pwdCtrl.clearValidators();
        pwdCtrl.setValue('');
      }
      pwdCtrl.updateValueAndValidity({ emitEvent: false });
    }

    if (changes['user'] && this.user) {
      this.form.patchValue(
        {
          firstName: this.user.firstName ?? '',
          lastName: this.user.lastName ?? '',
          sex: this.user.sex ?? '',
          username: this.user.username ?? '',
          email: this.user.email ?? '',
          phone: this.user.phone ?? '',
          address: this.user.address ?? '',
          active: this.user.active ?? true,
        },
        { emitEvent: false },
      );
    }
  }

  togglePassword(): void {
    this.showPassword = !this.showPassword;
  }

  onSubmit(): void {
    if (this.form.invalid || this.submitting) {
      this.form.markAllAsTouched();
      return;
    }
    const raw = this.form.getRawValue();

    // Build the payload based on mode, drop empty optionals.
    const base = {
      firstName: raw.firstName.trim(),
      lastName: raw.lastName.trim(),
      username: raw.username.trim(),
      email: raw.email.trim(),
      phone: raw.phone.trim(),
      ...(raw.sex ? { sex: raw.sex } : {}),
      ...(raw.address?.trim() ? { address: raw.address.trim() } : {}),
    };

    if (this.mode === 'create') {
      const payload: CreateUserRequest = {
        ...base,
        plainPassword: raw.plainPassword,
        active: raw.active,
      } as CreateUserRequest;
      this.save.emit(payload);
    } else {
      const payload: PatchUserRequest = {
        ...base,
        active: raw.active,
        ...(raw.plainPassword ? { plainPassword: raw.plainPassword } : {}),
      };
      this.save.emit(payload);
    }
  }

  onCancel(): void {
    this.cancel.emit();
  }

  // ── helpers used by template ─────────────────────────────────────────
  showError(name: keyof typeof this.form.controls): boolean {
    const c = this.form.controls[name];
    return c.invalid && (c.touched || c.dirty);
  }

  errorFor(name: keyof typeof this.form.controls): string | null {
    const c: AbstractControl = this.form.controls[name];
    if (!c.errors || (!c.touched && !c.dirty)) return null;
    if (c.errors['required']) return 'Champ obligatoire.';
    if (c.errors['email']) return 'Adresse email invalide.';
    if (c.errors['minlength']) {
      const need = c.errors['minlength'].requiredLength;
      return `${need} caractères minimum.`;
    }
    if (c.errors['maxlength']) {
      const max = c.errors['maxlength'].requiredLength;
      return `${max} caractères maximum.`;
    }
    if (c.errors['pattern']) {
      return 'Format invalide (lettres, chiffres, . _ - uniquement).';
    }
    return 'Valeur invalide.';
  }
}
